import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Dialog, IconButton, TextField, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { OBSIDIAN, OBSIDIAN_BODY_FONT, OBSIDIAN_DISPLAY_FONT } from '../../constants/obsidianDashboardTheme';
import { showToastMessage, useDispatchPromise } from '../../utils/AppUtils';
import { type SandboxOfficeHoursConfig } from '../../utils/sandboxOfficeHoursApi';
import {
    bookTeacherOfficeHours,
    fetchTeacherOfficeHours,
    fetchTeacherOfficeHoursAvailability,
} from '../../redux/sandbox/actions';
import { buildSandboxOfficeHoursPayment } from '../../utils/openSandboxOfficeHoursCheckout';
import type { SandboxCoursePaymentPayload } from '../../utils/openSandboxCourseCheckout';
import type { IProject } from '../../types/global/helper';
import { SandboxCoursePaymentModal } from './SandboxCoursePaymentModal';
import { SandboxMyOfficeHoursPanel } from './SandboxMyOfficeHoursPanel';
import { SandboxBookingMonthCalendar } from './SandboxBookingMonthCalendar';
import { SandboxPrivateProjectPicker } from './SandboxPrivateProjectPicker';
import type { BookingCalendarConfig } from '../../utils/sandboxBookingCalendar';

type Slot = {
    start: string;
    end: string;
    price?: {
        enabled?: boolean;
        amount?: number;
        currency?: string;
        free?: boolean;
        original_currency?: string;
        transaction_currency?: string;
        rate?: number;
        converted_amount?: number;
        formatted_for_display?: string | null;
        defaulted_to_usd?: boolean;
        student_total?: number;
        processor_fee_profile?: string;
        fee_preview?: import('../../utils/openSandboxCourseCheckout').SandboxFeePreview;
    };
};

type TabId = 'book' | 'sessions';

type Props = {
    open: boolean;
    teacherId: string;
    teacherName?: string;
    onClose: () => void;
    initialTab?: TabId;
    bookBlockedHint?: string;
};

function formatSlot(iso: string) {
    try {
        return new Intl.DateTimeFormat(undefined, {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
        }).format(new Date(iso));
    } catch {
        return iso;
    }
}

function formatSlotTime(iso: string) {
    try {
        return new Intl.DateTimeFormat(undefined, {
            hour: 'numeric',
            minute: '2-digit',
        }).format(new Date(iso));
    } catch {
        return iso;
    }
}

function priceLabel(slot: Slot) {
    if (!slot.price || slot.price.free || !slot.price.enabled || !(Number(slot.price.amount) > 0)) {
        return 'Free';
    }
    const catalog = Number(slot.price.amount);
    const local = slot.price.formatted_for_display?.trim();
    if (local && !slot.price.defaulted_to_usd && slot.price.transaction_currency !== 'usd') {
        return `$${catalog.toFixed(2)} · ${local}`;
    }
    const student = Number(slot.price.student_total) > 0 ? Number(slot.price.student_total) : catalog;
    return `$${student.toFixed(2)}`;
}

function tabButtonSx(active: boolean) {
    return {
        minWidth: 0,
        px: 2,
        py: 0.75,
        borderRadius: 999,
        textTransform: 'none' as const,
        fontFamily: OBSIDIAN_BODY_FONT,
        fontWeight: 700,
        fontSize: 13,
        color: active ? OBSIDIAN.onPrimaryFixed : OBSIDIAN.onSurfaceVariant,
        bgcolor: active ? OBSIDIAN.secondary : 'transparent',
        boxShadow: active ? '0 2px 10px rgba(0,253,193,0.25)' : 'none',
        '&:hover': {
            bgcolor: active ? OBSIDIAN.secondary : OBSIDIAN.surfaceContainerHigh,
        },
    };
}

/**
 * Student: Meeteor-style calendar booking + notes/project (no guest details — logged in).
 */
export function SandboxBookOfficeHoursModal({
    open,
    teacherId,
    teacherName,
    onClose,
    initialTab = 'book',
    bookBlockedHint,
}: Props) {
    const dispatchPromise = useDispatchPromise();
    const [tab, setTab] = useState<TabId>(initialTab);
    const [loading, setLoading] = useState(true);
    const [bookable, setBookable] = useState(false);
    const [hasConfig, setHasConfig] = useState(false);
    const [googleConnected, setGoogleConnected] = useState(false);
    const [calConfig, setCalConfig] = useState<BookingCalendarConfig | null>(null);
    const [title, setTitle] = useState('Office Hours');
    const [timezone, setTimezone] = useState(() => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC');
    const [month, setMonth] = useState(() => {
        const n = new Date();
        return { y: n.getFullYear(), m: n.getMonth() };
    });
    const [date, setDate] = useState<string | null>(null);
    const [slots, setSlots] = useState<Slot[]>([]);
    const [slotsLoading, setSlotsLoading] = useState(false);
    const [currencyInfo, setCurrencyInfo] = useState<{
        original_currency?: string;
        transaction_currency?: string;
        rate?: number;
    } | null>(null);
    const [selected, setSelected] = useState<Slot | null>(null);
    const [notes, setNotes] = useState('');
    const [project, setProject] = useState<IProject | null>(null);
    const [projectPickerOpen, setProjectPickerOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [payment, setPayment] = useState<SandboxCoursePaymentPayload | null>(null);
    const [booked, setBooked] = useState(false);
    const [confirmedMeet, setConfirmedMeet] = useState<string | null>(null);

    const loadProfile = useCallback(async () => {
        if (!teacherId) return;
        setLoading(true);
        try {
            const res: any = await dispatchPromise(fetchTeacherOfficeHours(teacherId));
            const cfg = (res?.config || null) as SandboxOfficeHoursConfig | null;
            setBookable(Boolean(res?.bookable));
            setHasConfig(Boolean(cfg));
            setGoogleConnected(Boolean(res?.google_connected));
            setTitle(cfg?.title || 'Office Hours');
            if (cfg?.timezone) setTimezone(cfg.timezone);
            setCalConfig(
                cfg
                    ? {
                          timezone: cfg.timezone,
                          min_notice_hours: cfg.min_notice_hours,
                          max_advance_days: cfg.max_advance_days,
                          availability: cfg.availability,
                          date_overrides: cfg.date_overrides,
                      }
                    : null,
            );
        } catch (e: any) {
            showToastMessage(e?.message || 'Office hours unavailable', 'error');
            setBookable(false);
            setHasConfig(false);
            setGoogleConnected(false);
            setCalConfig(null);
        } finally {
            setLoading(false);
        }
    }, [teacherId, dispatchPromise]);

    const loadSlots = useCallback(async () => {
        if (!teacherId || !date || !bookable) {
            setSlots([]);
            setCurrencyInfo(null);
            return;
        }
        setSlotsLoading(true);
        setSelected(null);
        try {
            const viewerTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
            const res: any = await dispatchPromise(
                fetchTeacherOfficeHoursAvailability({ teacherId, date, timezone: viewerTz }),
            );
            setSlots(res?.slots || []);
            setCurrencyInfo(res?.currency_info || null);
        } catch (e: any) {
            showToastMessage(e?.message || 'Could not load slots', 'error');
            setSlots([]);
            setCurrencyInfo(null);
        } finally {
            setSlotsLoading(false);
        }
    }, [teacherId, date, bookable, dispatchPromise]);

    useEffect(() => {
        if (open) {
            setTab(initialTab);
            setBooked(false);
            setConfirmedMeet(null);
            setNotes('');
            setProject(null);
            setPayment(null);
            setDate(null);
            setSlots([]);
            setCurrencyInfo(null);
            setSelected(null);
            const n = new Date();
            setMonth({ y: n.getFullYear(), m: n.getMonth() });
            void loadProfile();
        }
    }, [open, initialTab, loadProfile]);

    useEffect(() => {
        if (open && bookable && tab === 'book' && !bookBlockedHint && date) void loadSlots();
    }, [open, bookable, tab, bookBlockedHint, date, loadSlots]);

    const book = async () => {
        if (!selected || !teacherId) return;
        setBusy(true);
        try {
            const res: any = await dispatchPromise(
                bookTeacherOfficeHours({
                    teacherId,
                    body: {
                        start_time: selected.start,
                        notes: notes.trim() || undefined,
                        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                        project_id: project?._id ? String(project._id) : undefined,
                    },
                }),
            );
            const booking = res?.booking || res;
            if (res?.payment_required) {
                const quote = res.quote;
                const amount = Number(quote?.amount || selected.price?.amount || 0);
                const payload = buildSandboxOfficeHoursPayment({
                    quote: quote
                        ? {
                              config_id: quote.config_id,
                              start_time: quote.start_time || selected.start,
                              end_time: quote.end_time,
                              amount,
                              currency: quote.currency || selected.price?.currency || 'usd',
                              teacher_id: quote.teacher_id || teacherId,
                              notes: quote.notes,
                              project_id: quote.project_id,
                              project_title: quote.project_title,
                              timezone: quote.timezone,
                              booker_name: quote.booker_name,
                              booker_email: quote.booker_email,
                          }
                        : null,
                    // Legacy only: if a pending booking was somehow returned
                    bookingId: booking?.status === 'pending_payment' ? booking._id : undefined,
                    title,
                    amountUsd: amount,
                    currency: quote?.currency || selected.price?.currency || 'usd',
                    startLabel: formatSlot(selected.start),
                    conversion: selected.price
                        ? {
                              original_currency: selected.price.original_currency || 'usd',
                              transaction_currency:
                                  selected.price.transaction_currency || currencyInfo?.transaction_currency,
                              rate: selected.price.rate ?? currencyInfo?.rate,
                              converted_amount: selected.price.converted_amount,
                              formatted_for_display: selected.price.formatted_for_display,
                              defaulted_to_usd: selected.price.defaulted_to_usd,
                              fee_preview: selected.price.fee_preview,
                              processor_fee_profile: selected.price.processor_fee_profile,
                              student_total: selected.price.student_total,
                          }
                        : currencyInfo,
                });
                if (payload) setPayment(payload);
                return;
            }
            setConfirmedMeet(booking?.meet_link || null);
            setBooked(true);
            showToastMessage('Office hours booked — check your email for the Meet link', 'success');
        } catch (e: any) {
            showToastMessage(e?.message || 'Booking failed', 'error');
        } finally {
            setBusy(false);
        }
    };

    const unavailableMessage = bookBlockedHint
        ? bookBlockedHint
        : !hasConfig
        ? 'This instructor hasn’t set up office hours in Studio yet. Turning on the course perk isn’t enough — they also need weekly hours and Google Calendar connected.'
        : !googleConnected
        ? 'This instructor hasn’t connected Google Calendar yet, so slots can’t be booked.'
        : 'This instructor has not opened bookable office hours yet.';

    const viewerTz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

    return (
        <>
            <Dialog
                open={open && !payment}
                onClose={busy ? undefined : onClose}
                fullWidth
                maxWidth="sm"
                PaperProps={{
                    sx: {
                        bgcolor: OBSIDIAN.surfaceContainerHigh,
                        backgroundImage: 'none',
                        borderRadius: 3,
                        border: `1px solid ${OBSIDIAN.outline}44`,
                    },
                }}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: 2.5,
                        py: 2,
                        borderBottom: `1px solid ${OBSIDIAN.outline}33`,
                    }}>
                    <Box>
                        <Typography
                            sx={{ fontFamily: OBSIDIAN_DISPLAY_FONT, fontWeight: 700, color: OBSIDIAN.onSurface }}>
                            {title}
                        </Typography>
                        <Typography
                            sx={{ fontSize: 12, color: OBSIDIAN.onSurfaceVariant, fontFamily: OBSIDIAN_BODY_FONT }}>
                            {teacherName ? `With ${teacherName}` : '1:1 Google Meet'} · {timezone}
                        </Typography>
                    </Box>
                    <IconButton
                        onClick={onClose}
                        disabled={busy}
                        size="small"
                        sx={{ color: OBSIDIAN.onSurfaceVariant }}>
                        <CloseIcon />
                    </IconButton>
                </Box>

                <Box sx={{ px: 2.5, pt: 2 }}>
                    <Box
                        sx={{
                            display: 'inline-flex',
                            p: 0.5,
                            borderRadius: 999,
                            bgcolor: OBSIDIAN.surfaceContainer,
                            gap: 0.5,
                        }}>
                        {(
                            [
                                { id: 'book' as const, label: 'Book' },
                                { id: 'sessions' as const, label: 'My sessions' },
                            ] as const
                        ).map(t => (
                            <Button key={t.id} onClick={() => setTab(t.id)} sx={tabButtonSx(tab === t.id)}>
                                {t.label}
                            </Button>
                        ))}
                    </Box>
                </Box>

                {tab === 'sessions' ? (
                    <Box sx={{ p: 2.5, maxHeight: '70vh', overflowY: 'auto' }}>
                        <SandboxMyOfficeHoursPanel
                            active={open && tab === 'sessions'}
                            onBook={bookBlockedHint ? undefined : () => setTab('book')}
                        />
                    </Box>
                ) : loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <CircularProgress sx={{ color: OBSIDIAN.primary }} />
                    </Box>
                ) : bookBlockedHint || !bookable ? (
                    <Box sx={{ p: 2.5 }}>
                        <Typography sx={{ color: OBSIDIAN.onSurfaceVariant, fontFamily: OBSIDIAN_BODY_FONT }}>
                            {unavailableMessage}
                        </Typography>
                    </Box>
                ) : booked ? (
                    <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <Typography sx={{ color: OBSIDIAN.onSurface, fontFamily: OBSIDIAN_BODY_FONT, fontWeight: 600 }}>
                            You&apos;re booked
                        </Typography>
                        {confirmedMeet ? (
                            <Button
                                href={confirmedMeet}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                    alignSelf: 'flex-start',
                                    textTransform: 'none',
                                    fontWeight: 700,
                                    bgcolor: OBSIDIAN.primary,
                                    color: OBSIDIAN.onPrimaryFixed,
                                    borderRadius: 999,
                                    px: 2,
                                }}>
                                Join Meet
                            </Button>
                        ) : (
                            <Typography sx={{ fontSize: 13, color: OBSIDIAN.onSurfaceVariant }}>
                                Meet link will arrive by email shortly.
                            </Typography>
                        )}
                        <Button
                            onClick={() => setTab('sessions')}
                            sx={{ textTransform: 'none', color: OBSIDIAN.primary }}>
                            View my sessions
                        </Button>
                    </Box>
                ) : (
                    <Box
                        sx={{
                            p: 2.5,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                            maxHeight: '75vh',
                            overflowY: 'auto',
                        }}>
                        <SandboxBookingMonthCalendar
                            month={month}
                            onMonthChange={setMonth}
                            selectedDate={date}
                            onSelectDate={setDate}
                            config={calConfig}
                        />
                        <Typography
                            sx={{
                                mt: -1,
                                fontSize: 11,
                                color: OBSIDIAN.onSurfaceVariant,
                                fontFamily: OBSIDIAN_BODY_FONT,
                            }}>
                            Sessions can be booked up to 2 weeks ahead.
                        </Typography>

                        <Box>
                            <Typography
                                sx={{
                                    mb: 1,
                                    fontSize: 11,
                                    fontWeight: 700,
                                    letterSpacing: '0.08em',
                                    textTransform: 'uppercase',
                                    color: OBSIDIAN.onSurfaceVariant,
                                }}>
                                Select time
                            </Typography>
                            <Typography sx={{ mb: 1.25, fontSize: 12, color: OBSIDIAN.onSurfaceVariant }}>
                                Times in your local time ({viewerTz}).
                            </Typography>
                            {!date ? (
                                <Typography sx={{ fontSize: 13, color: OBSIDIAN.onSurfaceVariant }}>
                                    Choose a date to see times.
                                </Typography>
                            ) : slotsLoading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                                    <CircularProgress size={28} sx={{ color: OBSIDIAN.primary }} />
                                </Box>
                            ) : slots.length === 0 ? (
                                <Typography sx={{ fontSize: 13, color: OBSIDIAN.onSurfaceVariant }}>
                                    No open slots this day — try another date.
                                </Typography>
                            ) : (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {slots.map(slot => {
                                        const active = selected?.start === slot.start;
                                        return (
                                            <Button
                                                key={slot.start}
                                                onClick={() => setSelected(slot)}
                                                sx={{
                                                    textTransform: 'none',
                                                    borderRadius: 2,
                                                    border: `1px solid ${
                                                        active ? OBSIDIAN.primary : `${OBSIDIAN.outline}55`
                                                    }`,
                                                    bgcolor: active
                                                        ? `${OBSIDIAN.primary}22`
                                                        : OBSIDIAN.surfaceContainer,
                                                    color: OBSIDIAN.onSurface,
                                                    fontFamily: OBSIDIAN_DISPLAY_FONT,
                                                    fontWeight: 800,
                                                    fontSize: 13,
                                                    px: 1.5,
                                                    py: 1,
                                                }}>
                                                <Box component="span" sx={{ fontWeight: 800 }}>
                                                    {formatSlotTime(slot.start)}
                                                </Box>
                                                <Box
                                                    component="span"
                                                    sx={{
                                                        fontWeight: 600,
                                                        fontFamily: OBSIDIAN_BODY_FONT,
                                                        fontSize: 12,
                                                        color: OBSIDIAN.onSurfaceVariant,
                                                        ml: 0.75,
                                                    }}>
                                                    · {priceLabel(slot)}
                                                </Box>
                                            </Button>
                                        );
                                    })}
                                </Box>
                            )}
                        </Box>

                        <Box>
                            <Typography
                                sx={{
                                    mb: 1,
                                    fontSize: 11,
                                    fontWeight: 700,
                                    letterSpacing: '0.08em',
                                    textTransform: 'uppercase',
                                    color: OBSIDIAN.onSurfaceVariant,
                                }}>
                                Project (optional)
                            </Typography>
                            <Button
                                onClick={() => setProjectPickerOpen(true)}
                                sx={{
                                    width: '100%',
                                    justifyContent: 'flex-start',
                                    textTransform: 'none',
                                    borderRadius: 2,
                                    border: `1px solid ${OBSIDIAN.outline}55`,
                                    bgcolor: OBSIDIAN.surfaceContainer,
                                    color: project ? OBSIDIAN.onSurface : OBSIDIAN.onSurfaceVariant,
                                    fontFamily: OBSIDIAN_BODY_FONT,
                                    fontWeight: project ? 700 : 500,
                                    px: 1.5,
                                    py: 1.25,
                                }}>
                                {project?.title || 'Select a private project to review'}
                            </Button>
                            {project ? (
                                <Button
                                    onClick={() => setProject(null)}
                                    sx={{
                                        mt: 0.5,
                                        textTransform: 'none',
                                        color: OBSIDIAN.onSurfaceVariant,
                                        fontSize: 12,
                                    }}>
                                    Clear project
                                </Button>
                            ) : null}
                        </Box>

                        <TextField
                            label="Notes for instructor (optional)"
                            value={notes}
                            onChange={e => setNotes(e.target.value)}
                            multiline
                            minRows={2}
                            fullWidth
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    bgcolor: '#323232',
                                    color: OBSIDIAN.onSurface,
                                    '& fieldset': { borderColor: `${OBSIDIAN.outline}55` },
                                },
                                '& label': { color: OBSIDIAN.onSurfaceVariant },
                            }}
                        />

                        <Button
                            disabled={!selected || busy}
                            onClick={() => void book()}
                            sx={{
                                alignSelf: 'flex-end',
                                textTransform: 'none',
                                fontWeight: 800,
                                borderRadius: 999,
                                px: 2.5,
                                py: 1.25,
                                bgcolor: OBSIDIAN.primary,
                                color: OBSIDIAN.onPrimaryFixed,
                            }}>
                            {busy
                                ? 'Booking…'
                                : selected && !selected.price?.free && selected.price?.enabled
                                ? `Continue · ${priceLabel(selected)}`
                                : 'Book free slot'}
                        </Button>
                    </Box>
                )}
            </Dialog>

            <SandboxPrivateProjectPicker
                open={projectPickerOpen}
                onClose={() => setProjectPickerOpen(false)}
                onSelect={p => {
                    setProject(p);
                    setProjectPickerOpen(false);
                }}
                title="Project for this session"
            />

            {payment ? (
                <SandboxCoursePaymentModal
                    open
                    title="Office hours"
                    payload={payment}
                    category="sandbox_office_hours"
                    linkedEventType="CalendarBooking"
                    successMessage="Office hours confirmed — check your email for the Meet link"
                    onClose={() => setPayment(null)}
                    onSuccess={() => {
                        setPayment(null);
                        setConfirmedMeet(null);
                        setBooked(true);
                    }}
                />
            ) : null}
        </>
    );
}
